<template>
    <div class="container mx-auto">
        <!--Introduction-->
        <section class="bg-slate-50/60 my-4">
            <div class="mx-auto mb-20 lg:mb-0 lg:p-20">
                <div class="grid items-start gap-12 lg:grid-cols-2">
                    <!-- Left copy -->
                    <div>
                        <h6 class="text-blue-700 font-semibold tracking-wide uppercase text-xs">Tech33-Aktienpaket</h6>
                        <h1 class="font-extrabold">Chartanalyse der besten Tech-Aktien</h1>

                        <ul class="mt-8 space-y-4">
                            <FeatureItem title="Täglich aktuell" desc="Eine Analyse von drei bis vier Tech-Aktien jeden Handelstag" />
                            <FeatureItem title="33 hochkarätige Tech-Aktien" desc="Von Apple, Nvidia über Tesla bis zu Snowflake" />
                            <FeatureItem title="Klare Handelsmöglichkeiten" desc="Einstiegs- und Ausstiegszonen" />
                            <FeatureItem title="2-Wochen-Turnus" desc="Alle 2 Wochen analysieren wir für Sie alle 33 Aktien" />
                            <FeatureItem title="Portfoliomanagement" desc="Unser Musterportfolio macht unsere Performance und Handelsentscheidungen jederzeit nachvollziehbar" />
                        </ul>
                    </div>

                    <!-- Right pricing card -->
                    <div id="pricing-card">
                        <div class="overflow-hidden rounded-3xl bg-white shadow-xl ring-1 ring-slate-200">
                            <!-- Header -->
                            <div class="flex flex-col items-center gap-3 border-b border-slate-100 px-8 py-8 text-center">
                                <img class="w-20 h-auto object-cover bg-slate-100 rounded-full" :src="productGroup.image" />
                                <div class="space-y-1">
                                    <h3 class="text-lg font-semibold">Preisübersicht für das TECH33-Aktienpaket</h3>
                                    <p class="text-sm text-slate-500">Wählen Sie Ihre Laufzeit!</p>
                                </div>
                            </div>

                            <!-- Price options -->
                            <ul v-for="productVariant in sortedProductVariants" :key="productVariant.id" class="divide-y divide-slate-100">
                                <PriceRow
                                    :label="$translate(getAttributeByType(productVariant, 'period').title)"
                                    :badge="productVariant.discount_percent * 100 === 10 ? 'Beliebt' : ''"
                                    :note="$t('per_month')"
                                    :sub="productVariant.discount_percent > 0 ? '-' + $toPercent(productVariant.discount_percent, 0) : ''"
                                    :price="$toCurrency(productVariant.base_price_gross - productVariant.base_price_gross * productVariant.discount_percent)"
                                    :class="selectedProductVariant === productVariant ? 'bg-blue-dark text-white' : ''"
                                    @click="$emit('select-product', productVariant)"
                                />
                            </ul>

                            <!-- CTA -->
                            <div class="px-6 pb-6 pt-4">
                                <button
                                    type="button"
                                    class="group inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue px-5 py-3 text-sm font-semibold text-white shadow-md focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2"
                                    :class="{ 'opacity-50 cursor-not-allowed': isMaintenanceActive, 'hover:bg-slate-800': !isMaintenanceActive }"
                                    @click="isMaintenanceActive ? $router.push('/sorry') : $emit('add-to-cart')"
                                >
                                    <span>TECH33-Aktienpaket jetzt abonnieren</span>
                                    <i class="fi fi-rs-arrow-right flex align-middle text-white" />
                                </button>

                                <p class="mt-3 text-center text-xs text-slate-500">Sofort nach Buchung in Ihrem HKCM‑Dashboard und in unserer App abrufbar.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!--So funktioniert's-->
        <section class="mb-20 lg:mb-0 p-5 sm:p-10 lg:p-20 bg-gradient-to-b from-gray-100 to-gray-200 rounded-lg shadow-inner">
            <div class="max-w-6xl mx-auto">
                <!-- Header Block -->
                <div class="text-center mb-16">
                    <h2 class="text-2xl lg:text-3xl font-extrabold text-slate-900 leading-snug mb-6">So funktioniert unser TECH33-Paket</h2>
                    <p class="text-slate-600 max-w-4xl mx-auto text-sm lg:text-base">
                        Mit dem TECH33-Paket der HKCM erhalten Sie werktäglich (Montag bis Freitag) jeweils eine umfassende Analyse zu drei bis vier der wichtigsten
                        Technologieaktien der Welt. Das bedeutet, dass wir Ihnen in einem 2-Wochen-Turnus Analysen zu allen 33 Aktien aus dem TECH33-Paket übermitteln.<br />
                        <br />
                        Diese Analysen basieren auf der Elliott-Wellen-Analyse und zeigen Ihnen konkrete Zielzonen, sodass Sie auf einen Blick erkennen, wann Ein- und Ausstiege
                        oder eine Anpassung Ihrer Position sinnvoll sein kann. Ergänzend erhalten sie zu jedem Chart ein schriftliches Update, welches Ihnen unsere Analyse einfach
                        erklärt. Zur besseren Nachvollziehbarkeit führen wir ein Musterportfolio und informieren Sie transparent über alle vorgenommenen Anpassungen.
                    </p>
                </div>

                <!-- Features Block -->
                <div>
                    <h3 class="mt-12 mb-10 text-xl font-bold text-center text-slate-800">Das erhalten Sie konkret:</h3>

                    <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
                        <!-- Card 1 -->
                        <FeatureCard
                            title="Täglich eine schriftliche Analyse"
                            text="zu drei bis vier Tech-Aktien des TECH33-Pakets – direkt per E-Mail, im HKCM-Dashboard und in der App abrufbar."
                            :icon-class="'fi fi-rr-envelope-open-text'"
                        />

                        <!-- Card 2 -->
                        <FeatureCard
                            title="Klare Handelsmöglichkeiten"
                            text="mit präzisen Einstiegs- und Ausstiegszonen. Alle für den Handel relevanten Informationen erhalten Sie gesondert und übersichtlich."
                            :icon-class="'fi fi-rr-chart-line-up'"
                        />

                        <!-- Card 3 -->
                        <FeatureCard
                            title="Vollständige Analyse aller 33 Aktien"
                            text="Jede Aktie – von Apple über Nvidia bis Palantir – wird innerhalb eines 2-Wochen-Zyklus ausführlich analysiert."
                            :icon-class="'fi fi-rr-bullseye'"
                        />

                        <!-- Card 4 -->
                        <FeatureCard
                            title="Übersichtliche Chartbilder"
                            text="mit klar markierten Unterstützungen, Widerständen und Zielzonen – ideal für Long- und Short-Strategien."
                            :icon-class="'fi fi-rr-bolt'"
                        />

                        <!-- Card 5 -->
                        <FeatureCard
                            title="Schnelle Marktreaktionen"
                            text="inklusive Kurzupdates bei starken Kursbewegungen. Direkt per Push-Mitteilung in der App oder über das Dashboard."
                            :icon-class="'fi fi-rr-medal'"
                        />

                        <!-- Card 6 -->
                        <FeatureCard
                            title="Für alle Trader geeignet"
                            text="egal ob langfristig oder kurzfristig, ob Aktien, CFDs oder Derivate – inklusive klaren Einstiegs-, Ausstiegs- und Risikomarken."
                            :icon-class="'fi fi-rr-eye'"
                        />
                    </div>
                </div>
            </div>
        </section>

        <!--Aktien Liste-->
        <section class="w-full mb-20 lg:mb-0 p-5 sm:p-10 lg:p-20">
            <h2 class="text-center mb-4">Diese Tech-Aktien sind im TECH33-Paket enthalten:</h2>
            <div class="grid lg:grid-cols-2 gap-8 justify-items-start px-4">
                <div v-for="gruppe in gruppierteAktien" :key="gruppe.category" class="mb-4 w-full">
                    <!-- Kategoriename -->
                    <h3 class="text-xl mb-2 text-center">
                        {{ gruppe.category }}
                    </h3>

                    <!-- Aktien pro Kategorie -->
                    <div class="grid grid-cols-2 lg:grid-cols-3 gap-2">
                        <div
                            v-for="(aktie, idx) in gruppe.aktien"
                            :key="idx"
                            class="bg-blue text-white flex items-center px-3 py-1 rounded-lg w-full text-center justify-center lg:min-h-12 cursor-pointer"
                            @click="marketingModalOpen = aktie"
                        >
                            <span>{{ aktie.title }}</span>

                            <teleport to="body">
                                <Popup type="text" :overflow-hidden="true" :open="marketingModalOpen === aktie" @open="closeAction()">
                                    <p class="pt-4">{{ aktie.description }}</p>
                                </Popup>
                            </teleport>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!--Shopify-->
        <section
            v-if="productGroup.meta?.shopifyProductIds?.length > 0"
            class="lg:bg-gray-200 p-5 sm:p-10 lg:p-20 mb-20 lg:mb-0 bg-gradient-to-b from-gray-100 to-gray-200 rounded-lg shadow-inner"
        >
            <h2 class="text-center mb-4">Unsere Tassen</h2>
            <div class="gap-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 place-self-center justify-items-stretch">
                <div v-for="shopifyProductId in productGroup.meta?.shopifyProductIds" :key="shopifyProductId" class="w-full h-full flex">
                    <ShopifyProduct :product-id="shopifyProductId" bg-color="bg-white" class="h-full" />
                </div>
            </div>
        </section>

        <!--FAQ-->
        <section class="mb-20 lg:mb-0 p-5 sm:p-10 lg:p-20">
            <strong data-aos="fade-right">
                <h3 class="px-4 pt-10 text-center">Tech 33 - FAQ</h3>
            </strong>
            <div>
                <div v-for="(faq, key) in faqs" :key="key" data-aos="fade-up">
                    <Accordeon :title="faq.question" :content="faq.answer" />
                </div>
            </div>
        </section>

        <!--Tech Aktien Analyse-->
        <section class="py-12 md:py-16 lg:py-20 mb-20 lg:mb-0 p-5 sm:p-10 lg:p-20 bg-gradient-to-b from-gray-100 to-gray-200 rounded-lg shadow-inner">
            <div class="mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <!-- Textbereich -->
                <div>
                    <h2 class="mb-4">Tech-Aktien: Das bietet unsere Analyse</h2>
                    <p class="text-gray-600 mb-8">
                        Wir analysieren die Aktien von 33 führenden Technologieunternehmen anhand der Elliott-Wellen-Methodik und entwickeln daraus konkrete Handelsszenarien für
                        unterschiedliche Ansätze. <br /><br />

                        Die Analyse konzentriert sich dabei auf:
                    </p>

                    <div class="flex flex-col gap-6">
                        <FeatureItem title="Strukturiert aufgebaute Chartbilder" desc="mit klar markierten Einstiegs-, Ausstiegs-, Unterstützungs- und Widerstandsbereichen. " />
                        <FeatureItem title="Konkrete Zielzonen" desc="zur Orientierung bei Kauf-, Verkaufs- oder Positionsanpassungen. " />
                        <FeatureItem title="Transparenz durch Musterportfolio" desc="alle Einstiege, Ausstiege, Nachkäufe und Stopps werden offen nachvollziehbar dokumentiert. " />
                    </div>
                </div>

                <!-- Bildbereich -->
                <div class="relative">
                    <div class="overflow-hidden rounded-2xl shadow-lg bg-gradient-to-b from-gray-800 to-gray-700 flex items-center justify-center h-80">
                        <img :src="$asset('/hkcm/images/unsereAnalyse.png')" alt="Chart Illustration" class="object-cover lg:object-fill w-full h-full bg-white" />
                    </div>
                </div>
            </div>
        </section>

        <!--Tech Aktien Erklärung-->
        <section>
            <div class="mx-auto px-0 sm:px-6 lg:px-8 py-14 prose prose-slate max-w-none">
                <div class="mb-20 lg:mb-0 lg:p-20 flex flex-col-reverse lg:flex-row items-center gap-8">
                    <div class="lg:w-1/2 m-auto flex-col justify-center">
                        <h2>Was sind Tech Aktien?</h2>
                        <p class="text-slate-600">
                            Tech-Aktien sind Unternehmensanteile von Firmen, die ihr Kerngeschäft in Bereichen wie Software, KI, Hardware, Halbleiter, Internetdiensten,
                            IT-Infrastruktur oder digitaler Kommunikation haben. Diese Unternehmen treiben die technologische Entwicklung aktiv voran und profitieren gleichzeitig
                            stark vom digitalen Wandel in Wirtschaft und Gesellschaft.

                            <br /><br />

                            Besonders attraktiv sind Tech-Aktien, weil viele von ihnen eine beeindruckende Innovationskraft besitzen und regelmäßig neue Produkte und
                            Dienstleistungen auf den Markt bringen, die unser Leben nachhaltig verändern. Beispiele dafür sind Apple mit seinen iPhones, Microsoft mit seiner
                            dominierenden Position im Cloud-Bereich oder Nvidia, das mit seinen Chips den KI-Trend mitgestaltet.

                            <br /><br />

                            Tech-Unternehmen investieren überdurchschnittlich hohe Summen in Forschung und Entwicklung. Dadurch sichern sie sich Wettbewerbsvorteile, die in vielen
                            Fällen zu dauerhaften Erträgen führen. Gerade in Zeiten von Künstlicher Intelligenz, Strukturwandel, Digitalisierung und Automatisierung gelten
                            Tech-Aktien daher als vergleichsweise zukunftssicher und wachstumsstark. Sie sind nicht nur Impulsgeber für neue Trends, sondern können auch stabile
                            Säulen in einem ausgewogenen Portfolio sein.
                        </p>
                    </div>
                    <div class="lg:w-1/2 m-auto flex justify-center">
                        <img :src="$asset('/hkcm/images/tech33_three.jpg')" alt="Tech Aktien" class="w-auto rounded-lg shadow-lg" />
                    </div>
                </div>

                <div
                    class="bg-gradient-to-b from-gray-100 to-gray-200 rounded-lg shadow-inner mb-20 lg:mb-0 p-10 lg:p-20 flex flex-col-reverse lg:flex-row-reverse items-center gap-8"
                >
                    <div class="lg:w-1/2 m-auto flex-col-reverse justify-center">
                        <h2>Was sind Big Tech Aktien?</h2>
                        <p class="text-slate-600">
                            Als Big Tech werden die größten und einflussreichsten Technologiekonzerne der Welt bezeichnet. Diese Unternehmen prägen nicht nur den globalen
                            Aktienmarkt, sondern auch weite Teile unseres digitalen Alltags. Zu ihnen zählen Apple, Microsoft, Alphabet (Google), Amazon und Meta. Sie dominieren
                            ihre jeweiligen Branchen, verfügen über enorme Kapitalreserven und setzen mit ihrer Innovationskraft regelmäßig neue technologische Standards.

                            <br /><br />

                            Aufgrund ihrer globalen Reichweite und Marktstellung gelten sie als besonders robuste, wachstumsstarke Säulen in einem technologieorientierten
                            Portfolio. Ein weiterer Begriff, der in dem Zusammenhang mit Tech-Aktien neben Big Tech oft fällt, ist, Magnificent 7. Lesen Sie mehr dazu im nächsten
                            Absatz.
                        </p>
                    </div>
                    <div class="lg:w-1/2 m-auto flex justify-center">
                        <img :src="$asset('/hkcm/images/tech33_two.jpg')" alt="Tech Aktien" class="w-auto rounded-lg shadow-lg" />
                    </div>
                </div>

                <div class="mb-20 lg:mb-0 lg:p-20 flex flex-col-reverse lg:flex-row items-center gap-8">
                    <div class="lg:w-1/2 m-auto flex-col justify-center">
                        <h2>Was sind die Magnificent 7?</h2>
                        <p class="text-slate-600">
                            Magnificent 7 ist eine informelle Bezeichnung für die sieben führenden US-Technologieunternehmen: Apple, Microsoft, Alphabet, Amazon, Meta, Nvidia und
                            Tesla. Der Begriff hebt die außergewöhnliche Marktbedeutung dieser Konzerne hervor, da sie nicht nur in ihren Segmenten tonangebend sind, sondern auch
                            maßgeblich zur Performance der globalen Börsen beitragen. Gemeinsam vereinen sie Billionen an Marktkapitalisierung und sind häufig auch die Haupttreiber
                            großer Indizes – etwa im Technologie-Index Nasdaq 100 oder dem US-Leitindex S&P 500. Selbstverständlich sind alle sieben Unternehmen integraler
                            Bestandteil unseres TECH33-Pakets, da sie das Rückgrat der modernen Technologiebranche bilden und langfristig großes Potenzial besitzen. <br /><br />

                            Kurz zusammengefasst: Big Tech beschreibt die dominierenden Technologiekonzerne mit großer wirtschaftlicher und politischer Macht, während die
                            Magnificent 7 eine reine Aktiengruppe ist, die die stärksten Börsentreiber umfasst. Beide überschneiden sich, aber die Magnificent 7 sind breiter
                            gefasst und enthalten zusätzlich Nvidia und Tesla.
                        </p>
                    </div>
                    <div class="lg:w-1/2 m-auto flex justify-center">
                        <img :src="$asset('/hkcm/images/tech33_one.jpg')" alt="Tech Aktien" class="w-auto rounded-lg shadow-lg" />
                    </div>
                </div>
            </div>
        </section>

        <!--Tech Aktien USA-->
        <section class="mb-20 lg:mb-0 p-5 sm:p-10 lg:p-20 bg-gradient-to-b from-gray-100 to-gray-200 rounded-lg shadow-inner text-left">
            <h2 class="text-center">Tech Aktien USA</h2>
            <p class="text-slate-600 lg:w-3/4 m-auto">
                Die klare Mehrheit der Unternehmen in unserem TECH33-Paket stammt aus den Vereinigten Staaten. Genau genommen 31 von 33 Titeln. Die USA gelten nach wie vor als das
                Zentrum der globalen Tech-Industrie. Viele der weltweit einflussreichsten Technologieunternehmen sind dort ansässig und profitieren von einem
                innovationsfreundlichen Ökosystem, einem großen Binnenmarkt und starken Kapitalströmen für Forschung und Entwicklung.<br /><br />
                Selbst Grenzfälle wie Atlassian, ursprünglich in Australien gegründet, aber heute mit operativem Hauptsitz in San Francisco, oder Broadcom, mit US-Hauptsitz und
                internationaler Geschichte, unterstreichen die Bedeutung des US-Standorts. Einzige Ausnahmen im Paket sind Shopify aus Kanada und eben Atlassian, das je nach
                Definition zwischen Australien und den USA verortet werden kann. US-Tech-Aktien sind oft Marktführer und prägen globale Trends rund um Künstliche Intelligenz,
                Cloud-Technologie, Fintech und digitale Plattformen.
            </p>
        </section>

        <!--Wichtigkeit Tech Aktien-->
        <section class="mb-20 lg:mb-0 bg-slate-50">
            <div class="max-w-6xl mx-auto px-4 py-12 lg:py-20">
                <div class="grid gap-10 lg:grid-cols-2 lg:items-center">
                    <!-- Textbereich -->
                    <div>
                        <p class="text-sm font-semibold tracking-wide text-blue-600 text-center lg:text-left mb-2">Technologie als Wachstumsmotor</p>
                        <h2 class="text-2xl lg:text-3xl font-bold text-slate-900 text-center lg:text-left mb-6">
                            TECH33-Aktien: Die 33 wichtigsten Technologieaktien im Überblick
                        </h2>
                        <p class="text-slate-600 leading-relaxed text-sm lg:text-base lg:text-left">
                            Tech-Aktien gelten als besonders aussichtsreich, weil sie in unserer sich technologisch ständig verändernden Welt oft die treibende Kraft hinter
                            strukturellen Innovationen sind. Ob Digitalisierung, Automatisierung, künstliche Intelligenz oder nachhaltige Energie: Transformative Technologien
                            prägen nahezu alle Wirtschaftsbereiche und stammen häufig aus den Ideenschmieden der Magnificent 7.
                            <br /><br />
                            Unternehmen wie Nvidia, Apple oder Microsoft investieren jährlich Milliardenbeträge in Forschung und Entwicklung, um ihre führende Marktstellung zu
                            festigen und neue Wachstumsfelder zu erschließen. Viele Tech-Konzerne zeigen eine beeindruckende Fähigkeit zur Skalierung: Ein einmal entwickeltes
                            Softwareprodukt kann weltweit genutzt werden, was hohe Margen und stetige Einnahmequellen ermöglicht – ähnlich verhält es sich mit Prozessoren und
                            Chips.
                            <br /><br />
                            Zudem bieten viele Tech-Aktien trotz Wachstumspotenzial bereits heute solide Cashflows und – im Fall von Konzernen wie Apple oder Microsoft – sogar
                            regelmäßige Dividendenzahlungen. Gerade in unsicheren Zeiten können Tech-Aktien somit nicht nur als Innovationsmotor dienen, sondern auch Stabilität im
                            Depot bieten. Die globale Nachfrage nach digitalen Lösungen, Cloud-Diensten, Halbleitern oder Plattform-Technologien macht sie zu einem zukunftssicheren
                            Investment – vorausgesetzt, die Auswahl erfolgt gezielt und gut informiert – ein weiterer zentraler Service unseres Analysepakets.
                        </p>
                    </div>

                    <!-- Bildbereich -->
                    <div class="flex justify-center lg:justify-end">
                        <img
                            :src="$asset('/hkcm/images/mag_7_Animation.gif')"
                            alt="Animation zu TECH33-Technologieaktien"
                            class="w-full max-w-md rounded-2xl shadow-xl ring-1 ring-slate-200"
                        />
                    </div>
                </div>
            </div>
        </section>

        <!--Floating Jetzt Paket buchen button -->
        <div :class="!pricingCardInView ? 'opacity-100' : 'opacity-0'" class="fixed bottom-4 left-0 right-0 mx-auto w-fit z-50 transition duration-150 ease-in-out">
            <a href="#pricing-card" class="bg-blue text-white px-6 py-3 rounded-full shadow-lg flex items-center gap-3 hover:bg-blue-700 transition-colors ease-in-out">
                <span>Jetzt Paket buchen</span>
                <i class="fi fi-rs-shopping-cart text-white flex" />
            </a>
        </div>
    </div>
</template>

<script>
import FeatureItem from './FeatureItem.vue';
import PriceRow from './PriceRow.vue';
import FeatureCard from './FeatureCard.vue';
import ShopifyProduct from '../miscellaneous/ShopifyProducts.vue';
import Accordeon from '../miscellaneous/Accordeon.vue';
import Popup from '../miscellaneous/Popup.vue';
import tech33Aktien from '../../data/tech33_aktien_testdaten.json';
import { mapGetters } from 'vuex';

export default {
    name: 'Tech33AktienPaket',
    components: {
        FeatureItem,
        PriceRow,
        FeatureCard,
        ShopifyProduct,
        Accordeon,
        Popup
    },
    props: {
        productGroup: {
            type: Object,
            required: true
        },
        selectedProductVariant: {
            type: Object,
            default: null
        },
        sortedProductVariants: {
            type: Array,
            default: () => []
        }
    },
    data: () => ({
        pricingCardInView: false,
        marketingModalOpen: false,
        tech33Aktien,
        faqs: [
            {
                question: 'Was bekomme ich mit einem Abonnement des TECH 33 Pakets?',
                answer: 'Mit dem TECH33-Paket erhalten Sie von Montag bis Freitag täglich eine Analyse von jeweils drei bis vier Aktien-Titeln aus dem TECH33-Portfolio.0 Alle 2 Wochen übermitteln wir Ihnen somit Analysen zu allen 33 Titeln des TECH33-Pakets. Die Analysen beinhalten konkrete Zielzonen und verständlich erklärte Chartbilder auf Basis der Elliott-Wellen-Analyse. Zusätzlich informiert Sie die HKCM per Push-Benachrichtigung und E-Mail über außergewöhnliche Markbewegungen, Einstiege, Stoppsetzungen und Gewinnmitnahmen. Alle Inhalte stehen Ihnen stets und dauerhaft im HKCM-Dashboard und in unserer App zur Verfügung. Das integrierte Musterportfolio zeigt alle Einstiege, Ausstiege, Nachkäufe und Stopps. '
            },
            {
                question: 'Wie kann ich mein TECH 33 HKCM-Abonnement beenden?',
                answer: "Kündigen Sie Ihr Abonnement direkt in Ihrem HKCM-Dashboard. Loggen Sie sich ein, wählen Sie in der Menüleiste 'Profil', klicken Sie auf 'Abonnements' und anschließend auf 'Abonnements verwalten'. Dort können Sie mit einem Klick auf 'Kündigen' Ihr gewünschtes Abonnement beenden. Bitte beachten Sie die Kündigungsfrist von 5 Tagen vor Ende der gebuchten Laufzeit."
            },
            {
                question: 'Wie viel kostet das das TECH33-Aktienpaket?',
                answer: 'Das TECH33-Paket ist in vier Laufzeiten mit gestaffelten Monatspreisen erhältlich: <br/><br/>● Laufzeit 1 Monat: 99,95 € pro Monat <br/><br/>● Laufzeit 3 Monate: 94,95 € pro Monat (5 % Rabatt) <br/><br/>● Laufzeit 6 Monate: 89,96 € pro Monat (10 % Rabatt) <br/><br/>● Laufzeit 1 Jahr: 84,96 € pro Monat (15 % Rabatt). <br/><br/> Alle Abonnements verlängern sich nach Ablauf automatisch um die jeweils gebuchte Laufzeit. '
            },
            {
                question: 'Wie kann ich bei HKCM bezahlen?',
                answer: 'Die HKCM bietet folgende Zahlungsmethoden an:<br/><br/>● Kreditkarte (z. B. Mastercard, Visa)<br/><br/>● PayPal<br/><br/>● Apple Pay<br/><br/>● Google Pay<br/><br/>Alle Optionen stehen Ihnen direkt im Bestellprozess auf der Webseite zur Auswahl.'
            },
            {
                question: 'Kann ich das TECH 33 Paket von HKCM in der HKCM-App buchen?',
                answer: 'Unsere Pakete können ausschließlich über die HKCM-Webseite gebucht werden. In der App können Sie bestehende Abonnements einsehen und verwalten, aber kein neues TECH33-Abo abschließen. '
            },
            {
                question: 'Verlängert sich das TECH 33-Abonnement automatisch?',
                answer: 'Ja, das TECH33-Paket verlängert sich automatisch um die gewählte Laufzeit, wenn es nicht vorher gekündigt wird.'
            },
            {
                question: 'Was ist die HKCM?',
                answer: 'HKCM steht für Hopf-Klinkmüller Capital Management. Das Unternehmen bietet professionelle Finanzanalysen auf Basis der Elliott-Wellen-Theorie an. Unsere Analysepakete bieten Ihnen klare Marktanalysen zu Aktien, Indizes, Kryptowährungen und Rohstoffen. '
            }
        ],
        _pricingCardObserver: null
    }),
    computed: {
        ...mapGetters('settings', {
            isMaintenanceActive: 'isMaintenanceActive'
        }),
        gruppierteAktien() {
            const groups = {};
            this.tech33Aktien.info.forEach((aktie) => {
                if (!groups[aktie.category]) {
                    groups[aktie.category] = [];
                }

                groups[aktie.category].push(aktie);
            });

            return Object.entries(groups).map(([category, aktien]) => ({
                category,
                aktien
            }));
        }
    },
    mounted() {
        this.setupPricingObserver();
    },
    beforeUnmount() {
        if (this._pricingCardObserver) {
            this._pricingCardObserver.disconnect();
            this._pricingCardObserver = null;
        }
    },
    methods: {
        getAttributeByType(productVariant, type) {
            return productVariant.productAttributeValues.data.find((productAttributeValue) => {
                return productAttributeValue.meta.type === type;
            });
        },
        closeAction() {
            this.marketingModalOpen = false;
        },
        setupPricingObserver() {
            const el = document.getElementById('pricing-card');

            if (el) {
                this._pricingCardObserver = new IntersectionObserver(
                    (entries) => {
                        const entry = entries[0];
                        this.pricingCardInView = entry.isIntersecting;
                    },
                    { root: null, threshold: 0.1 }
                );
                this._pricingCardObserver.observe(el);
            } else {
                this.$nextTick(() => {
                    const el2 = document.getElementById('pricing-card');

                    if (el2) {
                        this._pricingCardObserver = new IntersectionObserver(
                            (entries) => {
                                this.pricingCardInView = entries[0].isIntersecting;
                            },
                            { root: null, threshold: 0.1 }
                        );
                        this._pricingCardObserver.observe(el2);
                    }
                });
            }
        }
    }
};
</script>
